import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    PiArrowsClockwise,
    PiArrowSquareOut,
    PiBuildings,
    PiCurrencyDollar,
    PiEye,
    PiIdentificationCard,
    PiMagnifyingGlass,
    PiMapPin,
    PiPlusCircle,
    PiSnowflake,
    PiUser,
    PiWarningCircle,
} from 'react-icons/pi';
import { Button, PageHeader, cn } from '@v2/design-system';
import { canInspectPloomesFields, canSyncPloomesDeal } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import type { PloomesDealDetail } from '../types';
import { ploomesDealPath } from '../dealPath';
import { contractBadgeClass, formatBrl } from '../utils';
import DealOwnerCell from '../DealOwnerCell';
import PloomesDealDetailTabs from './PloomesDealDetailTabs';

type Props = {
    deal: PloomesDealDetail;
    onSync: () => void;
    syncLoading: boolean;
    onCreateShipment: () => void;
    shipmentLoading: boolean;
    onTasksRefresh: () => void;
};

type ChipTone = 'neutral' | 'warning' | 'danger' | 'success' | 'info' | 'brand';
type ChipSize = 'md' | 'sm';

function formatPctLabel(rate: number | null | undefined): string | null {
    if (rate == null || Number.isNaN(Number(rate))) {
        return null;
    }
    let pct = Number(rate);
    if (pct > 0 && pct <= 1) {
        pct *= 100;
    }
    return pct.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function cleanClassificationLabel(value: string): string {
    return value.replace(/^\p{Extended_Pictographic}+\s*/u, '').trim() || value;
}

export default function PloomesDealDetailView({
    deal,
    onSync,
    syncLoading,
    onCreateShipment,
    shipmentLoading,
}: Props) {
    const { t } = useI18n();

    const exceptionPct = formatPctLabel(deal.commission_exception_rate);
    const teamPct = formatPctLabel(deal.team_agreement_pct);
    const carenciaMonths = deal.carencia_months != null ? Number(deal.carencia_months) : 0;
    const stageLabel = deal.stage_name
        ? (deal.pipeline_name ? `${deal.pipeline_name} · ${deal.stage_name}` : deal.stage_name)
        : null;
    const saleNumbers =
        deal.sale_numbers && deal.sale_numbers.length > 0
            ? deal.sale_numbers.map(String)
            : [
                  String(
                      deal.display_sale_number ||
                          (Number(deal.amount) > 0
                              ? deal.internal_number || deal.deal_number || String(deal.id)
                              : deal.deal_number || String(deal.id)),
                  ),
              ];
    const classificationLabel = deal.client_classification
        ? cleanClassificationLabel(deal.client_classification)
        : null;

    // Importação sempre entra na faixa de flags.
    const hasFlags = true;

    const hasMeta = Boolean(
        deal.sdr_name
        || deal.segment
        || deal.contact_state
        || carenciaMonths > 0,
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title={deal.title}
                breadcrumb={[
                    { name: 'Propostas Ganhas', href: '/ploomes-deals' },
                    { name: deal.title, href: ploomesDealPath(deal) },
                    { name: 'Visualizar' },
                ]}
            >
                {canSyncPloomesDeal() && (
                    <Button
                        size="sm"
                        className="bg-sky-500 text-white hover:bg-sky-600"
                        onClick={onSync}
                        isLoading={syncLoading}
                        title="Atualiza o deal no Hub a partir do Ploomes e recalcula a comissão (exceto se já houver NF enviada/paga)"
                    >
                        <PiArrowsClockwise className="me-1.5 h-4 w-4" />
                        Sincronizar com Ploomes
                    </Button>
                )}
                {canInspectPloomesFields() && (
                    <Link to={`/commissioning/ploomes-field-inspector?deal=${deal.id}`}>
                        <Button size="sm" as="span" className="bg-violet-500 text-white hover:bg-violet-600">
                            <PiMagnifyingGlass className="me-1.5 h-4 w-4" />
                            Inspecionar Campos
                        </Button>
                    </Link>
                )}
                <a href={deal.v1_urls.set_contract_type}>
                    <Button size="sm" as="span" className="bg-amber-500 text-white hover:bg-amber-600">
                        <PiIdentificationCard className="me-1.5 h-4 w-4" />
                        Definir contrato
                    </Button>
                </a>
                {deal.has_shipment && deal.shipment_id ? (
                    <Link to={`/shipments/${deal.shipment_id}`}>
                        <Button size="sm" as="span" className="bg-sky-600 text-white hover:bg-sky-700">
                            <PiEye className="me-1.5 h-4 w-4" />
                            Ver Importação
                        </Button>
                    </Link>
                ) : (
                    <Button
                        size="sm"
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        onClick={onCreateShipment}
                        isLoading={shipmentLoading}
                    >
                        <PiPlusCircle className="me-1.5 h-4 w-4" />
                        Criar Importação
                    </Button>
                )}
                <a href={deal.ploomes_url} target="_blank" rel="noreferrer">
                    <Button size="sm" as="span" className="bg-amber-500 text-white hover:bg-amber-600">
                        <PiArrowSquareOut className="me-1.5 h-4 w-4" />
                        Abrir no Ploomes
                    </Button>
                </a>
            </PageHeader>

            {/* 1) Identidade — número da venda sempre primeiro e em destaque */}
            <div className="flex flex-wrap items-center gap-2">
                {saleNumbers.map((n) => (
                    <Chip
                        key={n}
                        tone="brand"
                        emphasis
                        label={`#${n}`}
                        title={t('v2.ploomes_deals.badges.deal_number', { number: n })}
                    />
                ))}
                <Chip
                    emphasis
                    icon={<PiCurrencyDollar className="size-3.5" />}
                    label={formatBrl(deal.amount)}
                />
                {(deal.contact_name || deal.contact_legal_name) && (
                    <Chip
                        icon={<PiBuildings className="size-3.5" />}
                        label={deal.contact_name || deal.contact_legal_name || ''}
                    />
                )}
                {deal.owner_name && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">
                        <DealOwnerCell name={deal.owner_name} avatarUrl={deal.owner_avatar_url} />
                    </span>
                )}
                {stageLabel && <Chip label={stageLabel} />}
            </div>

            {/* 2) Flags de negócio */}
            {hasFlags && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2 dark:border-zinc-800">
                    {deal.client_classification && (
                        <Chip
                            tone={deal.client_classification_is_sad ? 'warning' : 'info'}
                            icon={<PiIdentificationCard className="size-3.5" />}
                            label={
                                deal.client_classification_is_sad
                                    ? t('v2.ploomes_deals.badges.client_classification_sad_short')
                                    : (classificationLabel ?? deal.client_classification)
                            }
                            title={deal.client_classification}
                        />
                    )}
                    {deal.commission_exception && (
                        <Chip
                            tone="warning"
                            icon={<PiWarningCircle className="size-3.5" />}
                            label={
                                exceptionPct
                                    ? t('v2.ploomes_deals.badges.exception_pct', { pct: exceptionPct })
                                    : t('v2.ploomes_deals.badges.exception')
                            }
                        />
                    )}
                    {deal.commission_frozen_legacy && (
                        <Chip
                            tone="info"
                            icon={<PiSnowflake className="size-3.5" />}
                            label={t('v2.ploomes_deals.badges.commission_frozen')}
                            title={t('v2.ploomes_deals.badges.commission_frozen_hint')}
                        />
                    )}
                    {deal.is_team_agreement && (
                        <Chip
                            tone="info"
                            label={
                                teamPct
                                    ? t('v2.ploomes_deals.badges.team_agreement_pct', { pct: teamPct })
                                    : t('v2.ploomes_deals.badges.team_agreement')
                            }
                        />
                    )}
                    {deal.has_defaulted_installment && (
                        <Chip tone="danger" label={t('v2.ploomes_deals.badges.defaulted')} />
                    )}
                    {deal.contract_type_label && (
                        <span
                            className={cn(
                                'inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                                contractBadgeClass(deal.contract_type_label),
                            )}
                        >
                            <span className="truncate">{deal.contract_type_label}</span>
                        </span>
                    )}
                    {deal.is_buckler_one_client && (
                        <Chip
                            tone="success"
                            label={t('v2.ploomes_deals.badges.buckler_one')}
                            title={deal.buckler_one_franchisor_name ?? undefined}
                        />
                    )}
                    {deal.is_fair_opportunity && (
                        <Chip
                            tone="info"
                            label={
                                deal.fair_name
                                    ? t('v2.ploomes_deals.badges.fair_named', { fair: deal.fair_name })
                                    : t('v2.ploomes_deals.badges.fair')
                            }
                            title={deal.fair_name ?? undefined}
                        />
                    )}
                    <Chip
                        tone={deal.has_shipment ? 'success' : 'warning'}
                        label={
                            deal.has_shipment
                                ? t('v2.ploomes_deals.badges.has_shipment')
                                : t('v2.ploomes_deals.badges.no_shipment')
                        }
                    />
                </div>
            )}

            {/* 3) Metadados secundários */}
            {hasMeta && (
                <div className="flex flex-wrap items-center gap-1.5">
                    {deal.sdr_name && (
                        <Chip
                            size="sm"
                            muted
                            icon={<PiUser className="size-3" />}
                            label={t('v2.ploomes_deals.badges.sdr', { name: deal.sdr_name })}
                        />
                    )}
                    {deal.segment && (
                        <Chip
                            size="sm"
                            muted
                            label={t('v2.ploomes_deals.badges.segment', { segment: deal.segment })}
                        />
                    )}
                    {deal.contact_state && (
                        <Chip
                            size="sm"
                            muted
                            icon={<PiMapPin className="size-3" />}
                            label={t('v2.ploomes_deals.badges.state', { state: deal.contact_state })}
                        />
                    )}
                    {carenciaMonths > 0 && (
                        <Chip
                            size="sm"
                            muted
                            label={t('v2.ploomes_deals.badges.carencia', { months: carenciaMonths })}
                        />
                    )}
                </div>
            )}

            <PloomesDealDetailTabs deal={deal} />
        </div>
    );
}

function Chip({
    icon,
    label,
    tone = 'neutral',
    title,
    size = 'md',
    emphasis = false,
    muted = false,
}: {
    icon?: ReactNode;
    label: string;
    tone?: ChipTone;
    title?: string;
    size?: ChipSize;
    emphasis?: boolean;
    muted?: boolean;
}) {
    const toneClass: Record<ChipTone, string> = {
        neutral:
            'border-gray-200 bg-white text-gray-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200',
        warning:
            'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300',
        danger:
            'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/40 dark:text-rose-300',
        success:
            'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
        info: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-300',
        brand:
            'border-orange-600 bg-orange-600 text-white shadow-sm dark:border-orange-500 dark:bg-orange-500 dark:text-white',
    };

    return (
        <span
            title={title}
            className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium',
                size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
                muted
                    ? 'border-transparent bg-gray-50 text-gray-500 dark:bg-zinc-800/60 dark:text-zinc-400'
                    : toneClass[tone],
                emphasis && tone !== 'brand' && 'border-gray-300 font-semibold text-gray-900 shadow-sm dark:border-zinc-600 dark:text-zinc-50',
                emphasis && tone === 'brand' && 'px-3 py-1 text-sm font-bold tracking-wide',
            )}
        >
            {icon}
            <span className="truncate">{label}</span>
        </span>
    );
}
